import { memo, useEffect, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { useToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { InfoPaneTopicId } from '../../services/ToolbarActionService';
import HelpPanelContent from '../common/HelpPanelContent';

/** Describes what the info pane should display: which help topic and for which content page. */
export interface InfoPaneDisplay {
  readonly topicId: InfoPaneTopicId;
  readonly currentPage: ContentNode | undefined;
}

/**
 * Displays contextual help content based on the topic requested via ToolbarActionService.
 */
const InfoPanel = memo(function InfoPanel() {
  const service = useToolbarActionService();
  const [display, setDisplay] = useState<InfoPaneDisplay | undefined>(() => {
    const topicId = service.infoPaneTopicId;
    return topicId ? { topicId, currentPage: service.infoPaneCurrentPage } : undefined;
  });

  // Re-sync local state when the service signals a topic change.
  useEffect(() => {
    const sync = () => {
      const topicId = service.infoPaneTopicId;
      setDisplay(topicId ? { topicId, currentPage: service.infoPaneCurrentPage } : undefined);
    };
    sync();
    return service.on('changed', sync);
  }, [service]);

  if (!display) {
    // If nothing to display.
    return HelpPanelContent.default();
  }

  // Grab the renderer declared in HelpPanelContent
  const infoRenderer = HelpPanelContent[display.topicId];

  // Render it. For now, we don't wrap ActionPanelBase, as the HelpPanelContent
  // has its own wrapper
  return infoRenderer(display.currentPage as ContentNode);
});

export default InfoPanel;
